interface EmailNotificationData {
  contactName: string;
  petName: string;
  whatsappLink?: string;
  interviewTime?: string;
  adoptionFee?: number;
  rejectionReason?: string;
  missingDocuments?: string[];
}

type NotificationType = 
  | 'application_submitted' |'application_approved' |'application_rejected' |'documents_required' | 'interview_scheduled';

export const emailNotificationService = {
  async sendNotification(
    type: NotificationType,
    recipientEmail: string,
    applicationData: EmailNotificationData
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const functionUrl = `${supabaseUrl}/functions/v1/send-adoption-notification`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          recipientEmail,
          applicationData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || 'Failed to send notification email'
        };
      }

      const result = await response.json();
      return { success: result.success, error: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send notification'
      };
    }
  },

  // Send application submitted notification
  async notifyApplicationSubmitted(
    email: string,
    contactName: string,
    petName: string
  ): Promise<{ success: boolean; error: string | null }> {
    return this.sendNotification('application_submitted', email, {
      contactName,
      petName
    });
  },

  // Send application approved notification
  async notifyApplicationApproved(
    email: string,
    contactName: string,
    petName: string,
    whatsappLink: string,
    interviewTime: string,
    adoptionFee: number
  ): Promise<{ success: boolean; error: string | null }> {
    return this.sendNotification('application_approved', email, {
      contactName,
      petName,
      whatsappLink,
      interviewTime,
      adoptionFee
    });
  },

  // Send application rejected notification
  async notifyApplicationRejected(
    email: string,
    contactName: string,
    petName: string,
    rejectionReason?: string
  ): Promise<{ success: boolean; error: string | null }> {
    return this.sendNotification('application_rejected', email, {
      contactName,
      petName,
      rejectionReason
    });
  },

  // Send documents required notification
  async notifyDocumentsRequired(
    email: string,
    contactName: string,
    petName: string,
    missingDocuments: string[]
  ): Promise<{ success: boolean; error: string | null }> {
    return this.sendNotification('documents_required', email, {
      contactName,
      petName,
      missingDocuments
    });
  },

  // Send interview scheduled notification
  async notifyInterviewScheduled(
    email: string,
    contactName: string,
    petName: string,
    interviewDate: string,
    whatsappLink?: string
  ): Promise<{ success: boolean; error: string | null }> {
    return this.sendNotification('interview_scheduled', email, {
      contactName,
      petName,
      interviewTime: interviewDate,
      whatsappLink
    });
  },

  // Backward compatibility wrappers
  async sendApplicationSubmittedEmail(
    application: any,
    email: string,
    contactName: string,
    petName: string
  ): Promise<{ success: boolean; error: string | null }> {
    return this.notifyApplicationSubmitted(
      email,
      contactName,
      petName
    );
  },

  async sendApplicationApprovedEmail(
    application: any,
    email: string,
    contactName: string,
    petName: string
  ): Promise<{ success: boolean; error: string | null }> {
    return this.notifyApplicationApproved(
      email,
      contactName,
      petName,
      application.whatsappNumber || '',
      application.interviewScheduledAt || 'To be scheduled',
      application.adoptionFee || 0
    );
  },

  async sendInterviewScheduledEmail(
    application: any,
    email: string,
    contactName: string,
    petName: string
  ): Promise<{ success: boolean; error: string | null }> {
    return this.notifyInterviewScheduled(
      email,
      contactName,
      petName,
      application.interviewScheduledAt || 'To be scheduled',
      application.interviewWhatsappLink
    );
  },

  async sendApplicationRejectedEmail(
    application: any,
    email: string,
    contactName: string,
    petName: string
  ): Promise<{ success: boolean; error: string | null }> {
    return this.notifyApplicationRejected(
      email,
      contactName,
      petName,
      application.rejectionReason
    );
  }
};